import { supabase } from '@/core/database/supabase';
import type { 
  ConstructionIssue, 
  IssueComment,
  IssueStats, 
  IssueFilters 
} from '../types/construction-issues.types';

export const ConstructionIssuesService = {
  /**
   * Danh sách vấn đề thi công
   */
  async getIssues(companyId: string, filters: IssueFilters = {}): Promise<{ data: ConstructionIssue[], total: number }> {
    let query = supabase
      .from('construction_issues')
      .select(`
        *,
        project:projects(id, name),
        zone:project_zones(id, name),
        reporter:user_profiles!reported_by(id, full_name, avatar_url:avatar_path),
        assignee:user_profiles!assigned_to_user_id(id, full_name, avatar_url:avatar_path),
        assigned_team:teams(id, name, code)
      `, { count: 'exact' })
      .eq('company_id', companyId);

    if (filters.status && filters.status !== 'all') query = query.eq('status_code', filters.status);
    if (filters.severity && filters.severity !== 'all') query = query.eq('severity_code', filters.severity);
    if (filters.projectId) query = query.eq('project_id', filters.projectId);
    if (filters.assignedTo) query = query.eq('assigned_to_user_id', filters.assignedTo);
    if (filters.search) {
      query = query.or(`title.ilike.%${filters.search}%,issue_code.ilike.%${filters.search}%`);
    }

    query = query.order('created_at', { ascending: false });

    const { data, error, count } = await query;

    if (error) throw error;
    return {
      data: (data || []) as ConstructionIssue[],
      total: count || 0
    };
  },

  /**
   * Chi tiết vấn đề
   */
  async getIssueDetail(id: string): Promise<ConstructionIssue> {
    const { data, error } = await supabase
      .from('construction_issues')
      .select(`
        *,
        project:projects(id, name),
        zone:project_zones(id, name),
        reporter:user_profiles!reported_by(id, full_name, avatar_url:avatar_path),
        assignee:user_profiles!assigned_to_user_id(id, full_name, avatar_url:avatar_path),
        assigned_team:teams(id, name, code)
      `)
      .eq('id', id)
      .single();

    if (error) throw error;
    return data as ConstructionIssue;
  },

  /**
   * Danh sách bình luận
   */
  async getIssueComments(issueId: string): Promise<IssueComment[]> {
    const { data, error } = await supabase
      .from('construction_issue_comments')
      .select(`
        *,
        author:user_profiles!created_by(id, full_name, avatar_url:avatar_path)
      `)
      .eq('issue_id', issueId)
      .order('created_at', { ascending: true });

    if (error) throw error;
    return data as IssueComment[];
  },

  /**
   * Thống kê vấn đề
   */
  async getIssueStats(companyId: string, projectId?: string): Promise<IssueStats> {
    let query = supabase.from('construction_issues').select('status_code, severity_code, created_at, resolved_at, sla_due_at').eq('company_id', companyId);
    if (projectId) query = query.eq('project_id', projectId);

    const { data, error } = await query;
    if (error) throw error;

    const openStatus = ['open', 'triaged', 'assigned', 'in_progress', 'waiting_material'];
    const openCount = data.filter(i => openStatus.includes(i.status_code)).length;
    const criticalCount = data.filter(i => i.severity_code === 'critical' || i.severity_code === 'high').length;
    
    // SLA breach check
    const now = new Date();
    const slaBreachCount = data.filter(i => 
      openStatus.includes(i.status_code) && 
      i.sla_due_at && 
      new Date(i.sla_due_at) < now
    ).length;

    return {
      totalIssues: data.length,
      openCount,
      criticalCount,
      avgResolutionTime: 0, // Simplified for now
      slaBreachCount,
    };
  },

  /**
   * CRUD
   */
  async createIssue(data: Partial<ConstructionIssue>): Promise<ConstructionIssue> {
    const { data: result, error } = await supabase
      .from('construction_issues')
      .insert([data])
      .select()
      .single();

    if (error) throw error;
    return result as ConstructionIssue;
  },

  async updateIssue(id: string, updates: Partial<ConstructionIssue>): Promise<void> {
    const { error } = await supabase
      .from('construction_issues')
      .update(updates)
      .eq('id', id);

    if (error) throw error;
  }
};

